import type { AssetManager } from "../../assets/asset-manager";
import type { CharacterDefinition } from "../../data/characters";
import type { PerfMonitor } from "../../perf/perf-monitor";
import type { ProgressiveFontLoader } from "../../app/font-loader";
import type { Scene } from "../scene";
import { HomeView } from "./home-view";

export interface HomeSceneCallbacks {
  getCharacter: () => CharacterDefinition;
  getCharacterIndex: () => number;
  changeCharacter: (direction: -1 | 1) => void;
  selectCharacter: (index: number) => void;
  play: () => void;
  formation: () => void;
}

export class HomeScene implements Scene {
  private view: HomeView | null = null;
  private characterRequest = 0;

  constructor(
    private readonly assets: AssetManager,
    private readonly backgroundLow: string,
    private readonly backgroundHigh: string,
    private readonly backgroundHighFallback: string,
    private readonly allCharacters: CharacterDefinition[],
    private readonly initialLowImage: string,
    private readonly callbacks: HomeSceneCallbacks,
    private readonly monitor: PerfMonitor,
    private readonly fonts: ProgressiveFontLoader,
    private readonly devMode: boolean,
  ) {}

  async mount(root: HTMLElement): Promise<void> {
    const current = this.callbacks.getCharacter();
    this.view = new HomeView(
      root,
      current,
      this.allCharacters,
      {
        onPrevious: () => this.callbacks.changeCharacter(-1),
        onNext: () => this.callbacks.changeCharacter(1),
        onSelectCharacter: (index) => this.callbacks.selectCharacter(index),
        onPlay: () => this.callbacks.play(),
        onFormation: () => this.callbacks.formation(),
      },
      this.monitor,
      this.initialLowImage,
    );
    this.view.setBackground(this.backgroundLow);
    this.view.setPosition(this.callbacks.getCharacterIndex(), this.allCharacters.length);
    this.view.setLoading("ready to explore");
    this.monitor.mark("home-interactive");
    this.monitor.measure("boot-to-interactive", "boot-start", "home-interactive");
    this.view.renderPerformance(this.devMode);
    this.fonts.startAfterInteractive();

    this.upgradeBackground();
    this.upgradeCharacter(current);
  }

  private async upgradeBackground(): Promise<void> {
    try {
      const highImage = await this.loadImageWithFallback([this.backgroundHigh, this.backgroundHighFallback], true);
      if (highImage) this.view?.setBackground(highImage);
    } catch {
      // Low background remains visible as the resilient fallback.
    }
  }

  private async loadImageWithFallback(urls: Array<string | undefined>, decode = false): Promise<string | undefined> {
    for (const url of urls) {
      if (!url) continue;
      try {
        const image = await this.assets.loadImage(url);
        if (decode && !url.toLowerCase().endsWith(".svg") && typeof image.decode === "function") {
          try {
            await image.decode();
          } catch {
            continue;
          }
        }
        return url;
      } catch {
        // Try the compatibility asset before giving up.
      }
    }
    return undefined;
  }

  private async upgradeCharacter(character: CharacterDefinition, request = ++this.characterRequest): Promise<void> {
    const highImage = await this.loadImageWithFallback([character.home.highImage, character.home.fallback?.highImage], true);
    if (request !== this.characterRequest || !highImage) return;
    this.view?.swapCharacterImage(highImage, true, character.home.placement);
    this.monitor.mark("home-high-visible");
    this.monitor.measure("interactive-to-home-high", "home-interactive", "home-high-visible");
    this.view?.renderPerformance(this.devMode);
  }

  showCharacter(character: CharacterDefinition, index: number): void {
    const request = ++this.characterRequest;
    this.view?.setCharacterMetadata(character);
    this.view?.setPosition(index, this.allCharacters.length);
    this.view?.setLoading("switching companion…");
    void this.loadImageWithFallback([character.home.lowImage, character.home.fallback?.lowImage]).then((lowImage) => {
      if (request !== this.characterRequest) return;
      if (lowImage) this.view?.showCharacterLow(lowImage, true, character.home.placement);
      return this.upgradeCharacter(character, request);
    }).then(() => {
      if (request === this.characterRequest) this.view?.setLoading("ready to explore");
    });
  }

  setLoading(message: string): void {
    this.view?.setLoading(message);
  }

  unmount(): void {
    this.view?.destroy();
    this.view = null;
  }
}
